
import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
} from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import { AxiosError } from "axios";
import { postData } from "../../utils/apiHelpers.ts";
import { toastError, toastSuccess } from "../../utils/helper";
/**
 * A custom hook for sending a POST request with React Query and Axios,
 * and invalidating queries after a successful POST.
 *
 * @template T - The type of the data being sent (request body).
 * @template R - The type of the response data.
 *
 * @param {string} url - The URL endpoint to send the POST request to.
 * @param {Omit<UseMutationOptions<ResponseModel<R>, unknown, T>, "mutationFn"> & { queryKeyToInvalidate?: string; config?: AxiosRequestConfig; }} [options]
 *        - Optional configuration for the mutation and Axios request.
 *        - `queryKeyToInvalidate`: The query key that needs to be invalidated after mutation.
 *        - `config`: Axios request configuration options.
 *
 * @returns {UseMutationResult<ResponseModel<R>, unknown, T>} - The result of the mutation, including the data, status, and error information.
 *
 * @example
 * ```typescript
 * const { mutate, isLoading, error } = usePost<User, ApiResponse>('/api/users', {
 *   queryKeyToInvalidate: 'usersList',
 *   config: { headers: { Authorization: 'Bearer token' } },
 * });
 * ```
 */
const usePost = <T, R>(
  options?: Omit<
    UseMutationOptions<R, unknown, { payload: T; url?: string }>,
    "mutationFn"
  > & {
    queryKeyToInvalidate?: QueryKey;
    config?: AxiosRequestConfig;
    successMessage?: string;
    errorMessage?: string;
  },
) => {
  const queryClient = useQueryClient(); // Access the React Query client

  return useMutation({
    mutationFn: async (data: { payload: T; url: string }) => {
      try {
        return await postData<T, R>(data.url, data.payload, options?.config);
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          if (error.response) {
            // Server responded with a status other than 2xx
            const message =
              error.response.data?.error?.message ||
              "An error occurred on the server.";
            toastError(message);
          } else if (error.request) {
            // Request was made but no response received
            toastError(
              "No response received from the server. Please check your network connection.",)
          }
        } else if (error instanceof Error) {
          toastError(
            error.message || "An unexpected error occurred. Please try again.",
          );
        } else {
          toastError("An unexpected error occurred.");

        }
        throw error; // Re-throw the error to ensure proper handling
      }
    },
    onSuccess: () => {
      // Invalidate the query after the mutation is successful
      if (options?.queryKeyToInvalidate) {
        queryClient.invalidateQueries({
          queryKey: options.queryKeyToInvalidate,
        });
      }
      // toastSuccess(
      //   options?.successMessage || "Data saved successfully."
      // );
    },
    onError: (error) => {
      if (
        error instanceof Error &&
        !(error instanceof AxiosError && error.response) &&
        !(error instanceof AxiosError && error.request)
      ) {
        toastError("An unexpected error occurred.");
      }
    },
    ...options,
  });
};

export default usePost;
